import React from "react";
import { Table, TableBody, TableCellText, Link } from "@ceridianhcm/components";
import "./YearEndBoxValueColumnsDefinition.css";
import { FormatCurrency } from "../../../common/formatting";
import { UploadCsv, downloadFileFromUrl } from "../../../services/ImportDetailService";
import logger from "../../../logger";

interface BoxValue {
  boxName: string;
  boxValue: string;
}

interface Props {
  boxValues: BoxValue[];
  boxHeader?: any[];
  containerTypeLookup?: Record<number, string>;
}

export interface ParentBoxValueSummaryRow {
  containerType: string;
  YearEndImportDetailId: string;
  totalFormCount: number;
  totalWageAmount: number;
  totalTaxAmount: number;
}

interface ParentBoxValueSummaryTableProps {
  companyName: string;
  fileName: string
  rows: ParentBoxValueSummaryRow[];
  onFileClick: (YearEndImportDetailId: ParentBoxValueSummaryRow) => void;
}

interface ExportButtonProps {
  onClick: () => void;
  disabled: boolean;
  ariaLabel: string;
  testId: string;
}

interface BoxValueRowData {
  box1: string;
  amount1: string;
  box2: string;
  amount2: string;
  box3: string;
  amount3: string;
  box4: string;
  amount4: string;
  box5: string;
  amount5: string;
}

const toBoxValueRowData = (formattedRows: string[][]): BoxValueRowData[] => {
  return formattedRows.map((row) => ({
    box1: row[0] || "", amount1: row[1] || "",
    box2: row[2] || "", amount2: row[3] || "",
    box3: row[4] || "", amount3: row[5] || "",
    box4: row[6] || "", amount4: row[7] || "",
    box5: row[8] || "", amount5: row[9] || "",
  }));
};

const boxValueColumnDefs = [
  { field: "box1", label: "Box/Code", cellTemplate: (ctx: any) => <TableCellText>{ctx.rowData.box1}</TableCellText> },
  { field: "amount1", label: "Amount", cellTemplate: (ctx: any) => <TableCellText><span className="box-value-amount">{ctx.rowData.amount1}</span></TableCellText> },
  { field: "box2", label: "Box/Code", cellTemplate: (ctx: any) => <TableCellText>{ctx.rowData.box2}</TableCellText> },
  { field: "amount2", label: "Amount", cellTemplate: (ctx: any) => <TableCellText><span className="box-value-amount">{ctx.rowData.amount2}</span></TableCellText> },
  { field: "box3", label: "Box/Code", cellTemplate: (ctx: any) => <TableCellText>{ctx.rowData.box3}</TableCellText> },
  { field: "amount3", label: "Amount", cellTemplate: (ctx: any) => <TableCellText><span className="box-value-amount">{ctx.rowData.amount3}</span></TableCellText> },
  { field: "box4", label: "Box/Code", cellTemplate: (ctx: any) => <TableCellText>{ctx.rowData.box4}</TableCellText> },
  { field: "amount4", label: "Amount", cellTemplate: (ctx: any) => <TableCellText><span className="box-value-amount">{ctx.rowData.amount4}</span></TableCellText> },
  { field: "box5", label: "Box/Code", cellTemplate: (ctx: any) => <TableCellText>{ctx.rowData.box5}</TableCellText> },
  { field: "amount5", label: "Amount", cellTemplate: (ctx: any) => <TableCellText><span className="box-value-amount">{ctx.rowData.amount5}</span></TableCellText> },
];

const sanitizeForCSV = (value: string) => {
  if (value == null) return "";
  const stringValue = String(value);
  if (/["\r\n,]/.test(stringValue)) {
    const escaped = stringValue.replace(/"/g, '""');
    return `"${escaped}"`;
  }
  return stringValue;
};

const getFormattedTimestamp = () => {
  return new Date().toISOString().replace(/[:.-]/g, '').slice(0, 15);
};

const generateAndDownloadCsv = async (
  baseFileName: string,
  csvContent: string,
  screenName: string = "Year-End Report"
) => {
  const formattedDate = getFormattedTimestamp();
  const csvfileName = `${baseFileName}_${formattedDate}.csv`;
  await downloadCsv(csvfileName, csvContent, screenName);
};

const getContainerType = (header: any, containerTypeLookup?: Record<number, string>): string => {
  if (!header) return "";
  const rawContainerType = header.ContainerType ?? header.containerType;
  return typeof rawContainerType === 'number' 
    ? containerTypeLookup?.[rawContainerType] || ""
    : rawContainerType || "";
};

const processBoxValuesIntoRows = (boxValues: BoxValue[], formatValue: (value: string, boxName: string) => string) => {
  const rows: string[][] = [];

  for (let i = 0; i < boxValues.length; i += 5) {
    const row: string[] = [];

    for (let j = 0; j < 5; j++) {
      const item = boxValues[i + j];
      if (item) {
        const boxName = sanitizeForCSV(item.boxName || "");
        const formattedValue = formatValue(item.boxValue, boxName);
        row.push(boxName, formattedValue);
      } else {
        row.push("", "");
      }
    }

    rows.push(row);
  }

  return rows;
};

const formatBoxValuesForDisplay = (boxValues: BoxValue[], boxHeader?: any[]) => {
  return processBoxValuesIntoRows(boxValues, (boxValue) => {
    const numericValue = parseFloat(boxValue);
    return numericValue > 0 ? FormatCurrency(numericValue) : "";
  });
};

const exportToCSV = async (boxValues: BoxValue[], boxHeader?: any[], containerTypeLookup?: Record<number, string>) => {
  const header = boxHeader?.[0];
  const reportContainerLabel = getContainerType(header, containerTypeLookup);
  const headerRow = [
    "Box/Code", "Amount",
    "Box/Code", "Amount",
    "Box/Code", "Amount",
    "Box/Code", "Amount",
    "Box/Code", "Amount"
  ];

  const rows = processBoxValuesIntoRows(boxValues, (boxValue) => {
    const numericValue = parseFloat(boxValue);
    return numericValue > 0 ? sanitizeForCSV(FormatCurrency(numericValue)) : "";
   });

  let csvContent = '';

  if (boxHeader && boxHeader.length > 0) {
    const headerData = boxHeader[0];

    csvContent += `${reportContainerLabel} Management Report  ${headerData.TaxYear}`;
    csvContent += `Run By:,${headerData.CreatedBy}\n`;
    csvContent += `Run Date:,${headerData.RunDate}\n\n`;
    csvContent += `Total number of forms:,${headerData.T4SlipCount}\n`;
    csvContent += `Grand Totals,\n`;
  }

  csvContent += [headerRow.join(","), ...rows.map(r => r.join(","))].join("\n");

  const baseFileName = reportContainerLabel ? `${reportContainerLabel}-Report` : "Year-EndReport";
  await generateAndDownloadCsv(baseFileName, csvContent);
};

// Export downloadCsv to be reused by other components (e.g., YearEndProcessing)
export const downloadCsv = async (csvfileName: string, csvContent: string, screenName: string) => {
  try {
    const uploadOutcome = await UploadCsv(csvfileName, csvContent, screenName);
    const resourceUrl = uploadOutcome.data;

    if (resourceUrl) {
      try {
        downloadFileFromUrl(resourceUrl, csvfileName);
      } catch (e) {
        logger.error("Invalid download URL");
      }
    } else {
      logger.error("No blob URL returned from server.");
    }
  } catch (error) {
    logger.error("Upload failed:", error);
    throw error;
  }
};

export const exportParentSummaryToCSV = async (fileName: string, rows: ParentBoxValueSummaryRow[]) => {
  if (!rows.length) {
    logger.warn("No parent summary rows available for export.");
    return;
  }

  const headers = [
    "Container Type",
    "Total number of forms",
    "Total Wage Amount",
    "Total Tax Amount",
  ];

  const csvRows = rows.map((row) => {
    return [
      sanitizeForCSV(row.containerType),
      row.totalFormCount,
      sanitizeForCSV(FormatCurrency(row.totalWageAmount || 0)),
      sanitizeForCSV(FormatCurrency(row.totalTaxAmount || 0)),
    ].join(",");
  });

  const contentRows = [
    `File Name:,${sanitizeForCSV(fileName)}`,
    "",
    headers.join(","),
    ...csvRows,
  ];

  const csvContent = contentRows.join("\n");
  await generateAndDownloadCsv("YearEndParentSummary", csvContent);
};

const ExportButton: React.FC<ExportButtonProps> = ({ onClick, disabled, ariaLabel, testId }) => {
  return (
      <button
        onClick={onClick}
        disabled={disabled}
        className="ye-export-btn"
        aria-label={ariaLabel}
        data-testid={testId}
      >
        <svg
          data-evr-name="export"
          viewBox="0 0 24 24"
          width={20}
          height={20}
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.2929 8.70711C15.6834 9.09763 16.3166 9.09763 16.7071 8.70711C17.0976 8.31658 17.0976 7.68342 16.7071 7.29289L12.7071 3.29289C12.3166 2.90237 11.6834 2.90237 11.2929 3.29289C11.2897 3.29603 11.2866 3.29918 11.2835 3.30235L7.29289 7.29299C6.90237 7.68351 6.90237 8.31668 7.29289 8.7072C7.68342 9.09772 8.31658 9.09772 8.70711 8.7072L11 6.41431V14C11 14.5523 11.4477 15 12 15C12.5523 15 13 14.5523 13 14L13 6.41424L15.2929 8.70711ZM2 13C2 12.4477 2.44772 12 3 12C3.55228 12 4 12.4477 4 13V19H20V13C20 12.4477 20.4477 12 21 12C21.5523 12 22 12.4477 22 13V19C22 20.1046 21.1046 21 20 21H4C2.89543 21 2 20.1046 2 19V13Z"
          />
        </svg>
        <span className="ye-export-label">Export</span>
      </button>
  );
};

const BoxValueTable: React.FC<Props> = ({ boxValues, boxHeader, containerTypeLookup }) => {
  const formattedRows = formatBoxValuesForDisplay(boxValues, boxHeader);
  const tableData = toBoxValueRowData(formattedRows);
  const reportContainerLabel = getContainerType(boxHeader?.[0], containerTypeLookup);

  return (
    <div>
      {/* Header Section */}
      <div className="report-header-title-row">
        <div><span>{boxHeader?.[0]?.CompanyName}</span></div>
        <div><h3>{reportContainerLabel} Management Report {boxHeader?.[0]?.TaxYear}</h3></div>
        <ExportButton
          onClick={() => exportToCSV(boxValues, boxHeader, containerTypeLookup)}
          disabled={boxValues.length === 0}
          ariaLabel="Export Year-End Report"
          testId="year-end-export-btn"
        />
      </div>

      <div className="ye-file-name-row">
        <div>
          <strong>File Name:</strong> <span>{boxHeader?.[0]?.FileName}</span>
        </div>
        <div style={{ display: 'flex', gap: '20px' }}>
          <div>
            <strong>Run By:</strong> <span>{boxHeader?.[0]?.CreatedBy}</span>
          </div>
          <div>
            <strong>Run Date:</strong> <span>{boxHeader?.[0]?.RunDate}</span>
          </div>
        </div>
      </div>

      {/* Summary section */}
      <div className="box-value-summary">
        <div className="box-value-summary-row">Total number of forms: {boxHeader?.[0]?.T4SlipCount}</div>
      </div>

      {/* Everest Table */}
      <div className="box-value-table-container">
        <Table id="box-value-everest-table">
          <TableBody
            id="box-value-table-body"
            testId="box-value-table-body"
            data={tableData}
            columnDefs={boxValueColumnDefs}
            ariaLabel="Year-End Box Values Table"
            rowDensity="compact"
            alternatingRowColors
            textMap={{ loading: "Loading..." }}
          />
        </Table>
      </div>
    </div>
  );
};

const getParentSummaryColumnDefs = (onFileClick: (row: ParentBoxValueSummaryRow) => void) => [
  {
    field: "containerType",
    label: "Container Type",
    cellTemplate: (ctx: any) => (
      <Link
        id={`container-type-${ctx.rowData.containerType}`}
        onClick={() => onFileClick(ctx.rowData)}
        variant="neutral"
        testId={`container-type-${ctx.rowData.containerType}`}
      >
        {ctx.rowData.containerType}
      </Link>
    ),
  },
  {
    field: "totalFormCount",
    label: "Total number of forms",
    cellTemplate: (ctx: any) => <TableCellText><span className="box-value-amount">{ctx.rowData.totalFormCount}</span></TableCellText>,
  },
  {
    field: "totalWageAmount",
    label: "Total Wage Amount",
    cellTemplate: (ctx: any) => <TableCellText><span className="box-value-amount">{FormatCurrency(ctx.rowData.totalWageAmount || 0)}</span></TableCellText>,
  },
  {
    field: "totalTaxAmount",
    label: "Total Tax Amount",
    cellTemplate: (ctx: any) => <TableCellText><span className="box-value-amount">{FormatCurrency(ctx.rowData.totalTaxAmount || 0)}</span></TableCellText>,
  },
];

export const ParentBoxValueSummaryTable: React.FC<ParentBoxValueSummaryTableProps> = ({ companyName, fileName, rows, onFileClick }) => {
  const parentSummaryColumnDefs = getParentSummaryColumnDefs(onFileClick);

  return (
    <div>
      <div className="report-header-title-row">
        <div><span>{companyName}</span></div>
        <div><h3>File Container Summary</h3></div>
        <ExportButton
          onClick={() => exportParentSummaryToCSV(fileName, rows)}
          disabled={rows.length === 0}
          ariaLabel="Export Parent Summary"
          testId="parent-summary-export-btn"
        />
      </div>
      <div className="ye-file-name-row">
        <div>
          <strong>File Name:</strong> <span>{fileName}</span>
        </div>
      </div>

      <div className="box-value-table-container">
        <Table id="parent-summary-everest-table">
          <TableBody
            id="parent-summary-table-body"
            testId="parent-summary-table-body"
            data={rows}
            columnDefs={parentSummaryColumnDefs}
            ariaLabel="Year-End Parent Summary Table"
            rowDensity="compact"
            alternatingRowColors
            textMap={{ loading: "Loading..." }}
          />
        </Table>
      </div>
    </div>
  );
};

export default BoxValueTable;
